"use client";

import React from "react";
import Link from "next/link";
import Image from "next/image";
import { Mail, Facebook } from "lucide-react";

import { useLocalePath } from "@/lib/useLocale";
import { useDictionary } from "@/lib/useDictionary";

interface FooterProps {
  className?: string;
}

export default function Footer({ className }: FooterProps) {
  const lp = useLocalePath();
  const dict = useDictionary();

  const footerLinks = {
    product: {
      title: dict.footer.sectionProduct,
      links: [
        { label: dict.footer.linkPlans, href: "/plans" },
        { label: dict.footer.linkTutorial, href: "/tutorial" },
      ],
    },
    company: {
      title: dict.footer.sectionCompany,
      links: [
        { label: dict.footer.linkAbout, href: "/about" },
        { label: dict.footer.linkArticles, href: "/articles" },
        {
          label: dict.footer.linkContact,
          href: "https://line.me/R/ti/p/@070xojro?ts=12171155&oat_content=url",
        },
      ],
    },
    support: {
      title: dict.footer.sectionSupport,
      links: [
        { label: dict.footer.linkRefund, href: "/refund" },
        { label: dict.footer.linkTerms, href: "/term" },
        { label: dict.footer.linkPrivacy, href: "/policy" },
      ],
    },
  };

  return (
    <footer className={"gradient-blue " + (className ?? "")}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2">
            <Link href={lp("/")} className="flex items-center gap-2 mb-6">
              <Image
                src="/icon/logo.png"
                alt={dict.footer.logoText}
                width={28}
                height={28}
                className="w-7 h-7 object-contain"
              />
              <span className="text-xl font-bold text-gray-600">{dict.footer.logoText}</span>
            </Link>
            <p className="text-gray-500 mb-6 max-w-sm">{dict.footer.tagline}</p>

            <div className="space-y-3 text-sm text-gray-500">
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4" />
                <a href="mailto:spyder@spyder.com.tw" className="hover:text-white transition-colors">
                  spyder@spyder.com.tw
                </a>
              </div>

              <div className="flex items-center gap-3">
                <Facebook className="w-4 h-4" />
                <a
                  href="https://www.facebook.com/profile.php?id=61560196705693"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white transition-colors"
                >
                  {dict.footer.facebookPage}
                </a>
              </div>

              <div className="text-gray-500">{dict.footer.taxId}</div>
            </div>
          </div>

          {Object.entries(footerLinks).map(([key, section]) => (
            <div key={key}>
              <h3 className="font-semibold text-gray-600 mb-4">{section.title}</h3>
              <ul className="space-y-3">
                {section.links.map((link) => (
                  <li key={link.href}>
                    <Link
                      href={link.href.startsWith("/") ? lp(link.href) : link.href}
                      className="text-sm text-gray-500 hover:text-white transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="py-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-gray-500">© {new Date().getFullYear()} {dict.footer.copyright}</p>
        </div>
      </div>
    </footer>
  );
}
